/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useMemo } from 'react'
import styled, { useTheme } from 'styled-components'
import Chart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'
import {
  IoChevronForward,
  IoFolderOutline,
  IoSearchOutline,
  IoInformationCircleOutline,
  IoLayersOutline,
  IoCheckmarkCircle,
  IoAlertCircle,
  IoWarning,
  IoRemoveCircle,
  IoExpandOutline,
  IoContractOutline,
  IoBarChartOutline,
  IoCloseOutline,
} from 'react-icons/io5'

import ArrowRed from '../../assets/icons/arrowred.png'
import ArrowGree from '../../assets/icons/Arrowgreen.png'
import Neutro from '../../assets/icons/Minus.png'

export interface PerformanceSku {
  ean: string
  sku: string
  descricao: string
  marca?: string
  un_cx?: number
  atual: number
  anterior: number
  sellout3M: number
  vendaMedia: number
  cobertura: number
  dias: number
  status: 'Equilibrado' | 'Excesso' | 'Ruptura' | 'Sem Giro'
}

export interface PerformanceFamilia {
  familia: string
  atual: number
  anterior: number
  sellout3M: number
  vendaMedia: number
  percentual: number
  tendencia: 'UP' | 'DOWN' | 'NEUTRAL'
  cobertura: number
  dias: number
  status: 'Equilibrado' | 'Excesso' | 'Ruptura' | 'Sem Giro'
  totalSkus: number
  skus: PerformanceSku[]
}

export interface PerformanceCategoria {
  categoria: string
  atual: number
  anterior: number
  sellout3M: number
  vendaMedia: number
  percentual: number
  tendencia: 'UP' | 'DOWN' | 'NEUTRAL'
  cobertura: number
  dias: number
  status: 'Equilibrado' | 'Excesso' | 'Ruptura' | 'Sem Giro'
  totalFamilias: number
  totalSkus: number
  familias: PerformanceFamilia[]
}

interface HierarchyCardsProps {
  data: PerformanceCategoria[]
  onOpenInfoModal?: () => void
}

type StatusFilterType = 'TODOS' | 'Equilibrado' | 'Excesso' | 'Ruptura' | 'Sem Giro'

// --- COMPONENTE INTERNO DE GRÁFICO COMPARATIVO POR CARD ---
interface ChartItemData {
  label: string
  estoque: number
  selloutMedio: number
  cobertura?: number
  dias?: number
}

interface EmbeddedChartProps {
  title: string
  subtitle?: string
  items: ChartItemData[]
  onClose: () => void
}

function EmbeddedCardChart({ title, subtitle, items, onClose }: EmbeddedChartProps) {
  const categories = items.map((i) => i.label)
  const estoques = items.map((i) => Math.round(i.estoque))
  const sellouts = items.map((i) => Math.round(i.selloutMedio))

  const series = [
    {
      name: 'Estoque Atual (Cx)',
      data: estoques,
    },
    {
      name: 'Sellout Médio 3M (Cx/mês)',
      data: sellouts,
    },
  ]

  const colors = ['#a6c10b', '#4cc9f0']
  const minWidth = Math.max(categories.length * 110, 480)

  const options: ApexOptions = {
    chart: {
      type: 'bar',
      height: 270,
      toolbar: { show: false },
      fontFamily: 'Montserrat, sans-serif',
      zoom: { enabled: false },
      animations: { enabled: true, speed: 400 },
      background: 'transparent',
    },
    colors: colors,
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: categories.length > 8 ? '70%' : '50%',
        borderRadius: 4,
        dataLabels: { position: 'top' },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => {
        if (!val || val === 0) return ''
        return val >= 1000 ? `${(val / 1000).toFixed(1)}k` : val.toLocaleString('pt-BR')
      },
      offsetY: -18,
      style: {
        fontSize: '9px',
        fontWeight: 'bold',
        colors: ['#cbd5e1'],
      },
      background: {
        enabled: true,
        foreColor: '#000',
        borderRadius: 3,
        padding: 3,
        borderWidth: 0,
        opacity: 0.7,
      },
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories: categories,
      labels: {
        style: { colors: '#cbd5e1', fontSize: '10px', fontWeight: 600 },
        rotate: -35,
        rotateAlways: categories.length > 3,
        trim: true,
        maxHeight: 70,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { colors: '#8e9aaf', fontSize: '10px' },
        formatter: (val: number) => val.toLocaleString('pt-BR'),
      },
    },
    grid: {
      borderColor: 'rgba(255, 255, 255, 0.06)',
      strokeDashArray: 3,
      padding: { top: 25, right: 15, left: 10, bottom: 0 },
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      fontSize: '11px',
      labels: { colors: '#cbd5e1' },
      markers: { shape: 'square' },
    },
    tooltip: {
      theme: 'dark',
      y: {
        formatter: (val: number) => `${val.toLocaleString('pt-BR')} Cx`,
      },
    },
  }

  return (
    <ChartContainer onClick={(e) => e.stopPropagation()}>
      <ChartHeader>
        <div className='title-area'>
          <h6>
            <IoBarChartOutline size={15} style={{ color: '#a6c10b' }} /> {title}
          </h6>
          {subtitle && <span className='sub'>{subtitle}</span>}
        </div>
        <CloseChartBtn onClick={onClose} title='Fechar gráfico'>
          <IoCloseOutline size={18} />
        </CloseChartBtn>
      </ChartHeader>
      <ChartScrollWrapper>
        <div style={{ width: `${minWidth}px`, minWidth: `${minWidth}px` }}>
          <Chart options={options} series={series} type='bar' height={270} width='100%' />
        </div>
      </ChartScrollWrapper>
    </ChartContainer>
  )
}

export default function HierarchyCards({ data, onOpenInfoModal }: HierarchyCardsProps) {
  const theme = useTheme() as any
  const isLight = theme?.name === 'light'
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilterType>('TODOS')
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())
  const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(new Set())

  // Controle de abertura dos gráficos contextuais
  const [categoryChartOpen, setCategoryChartOpen] = useState<Set<string>>(new Set())
  const [familyChartOpen, setFamilyChartOpen] = useState<Set<string>>(new Set())

  // Filtragem e busca reativa
  const filteredData = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()

    return data
      .map((cat) => {
        // Filtrar famílias
        const matchingFamilias = cat.familias
          .map((fam) => {
            // Filtrar SKUs
            const matchingSkus = (fam.skus || []).filter((sku) => {
              const matchesSearch =
                !term ||
                sku.descricao.toLowerCase().includes(term) ||
                sku.sku.toLowerCase().includes(term) ||
                sku.ean.toLowerCase().includes(term) ||
                (sku.marca && sku.marca.toLowerCase().includes(term)) ||
                fam.familia.toLowerCase().includes(term) ||
                cat.categoria.toLowerCase().includes(term)

              const matchesStatus =
                statusFilter === 'TODOS' || sku.status === statusFilter

              return matchesSearch && matchesStatus
            })

            const famMatchesDirectly =
              fam.familia.toLowerCase().includes(term) ||
              cat.categoria.toLowerCase().includes(term)

            const famMatchesStatus =
              statusFilter === 'TODOS' || fam.status === statusFilter

            const keepFam =
              matchingSkus.length > 0 ||
              (!term && famMatchesStatus) ||
              (famMatchesDirectly && (statusFilter === 'TODOS' || famMatchesStatus))

            if (!keepFam) return null

            return {
              ...fam,
              skus: matchingSkus.length > 0 ? matchingSkus : fam.skus,
            }
          })
          .filter(Boolean) as PerformanceFamilia[]

        const catMatchesDirectly = cat.categoria.toLowerCase().includes(term)
        const catMatchesStatus =
          statusFilter === 'TODOS' || cat.status === statusFilter

        const keepCat =
          matchingFamilias.length > 0 ||
          (!term && catMatchesStatus) ||
          (catMatchesDirectly && (statusFilter === 'TODOS' || catMatchesStatus))

        if (!keepCat) return null

        return {
          ...cat,
          familias: matchingFamilias,
        }
      })
      .filter(Boolean) as PerformanceCategoria[]
  }, [data, searchTerm, statusFilter])

  // Quando o usuário busca algo, abre automaticamente as categorias e famílias com correspondência
  React.useEffect(() => {
    if (searchTerm.trim()) {
      const allCats = new Set<string>()
      const allFams = new Set<string>()

      filteredData.forEach((cat) => {
        allCats.add(cat.categoria)
        cat.familias.forEach((fam) => {
          allFams.add(`${cat.categoria}__${fam.familia}`)
        })
      })

      setExpandedCategories(allCats)
      setExpandedFamilies(allFams)
    }
  }, [searchTerm, filteredData])

  const toggleCategory = (catName: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(catName)) {
        next.delete(catName)
      } else {
        next.add(catName)
      }
      return next
    })
  }

  const toggleFamily = (catName: string, famName: string) => {
    const key = `${catName}__${famName}`
    setExpandedFamilies((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const toggleCategoryChart = (catName: string) => {
    setCategoryChartOpen((prev) => {
      const next = new Set(prev)
      if (next.has(catName)) {
        next.delete(catName)
      } else {
        next.add(catName)
      }
      return next
    })
  }

  const toggleFamilyChart = (catName: string, famName: string) => {
    const key = `${catName}__${famName}`
    setFamilyChartOpen((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const handleExpandAll = () => {
    const allCats = new Set<string>()
    const allFams = new Set<string>()
    filteredData.forEach((cat) => {
      allCats.add(cat.categoria)
      cat.familias.forEach((fam) => {
        allFams.add(`${cat.categoria}__${fam.familia}`)
      })
    })
    setExpandedCategories(allCats)
    setExpandedFamilies(allFams)
  }

  const handleCollapseAll = () => {
    setExpandedCategories(new Set())
    setExpandedFamilies(new Set())
    setCategoryChartOpen(new Set())
    setFamilyChartOpen(new Set())
  }

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'Equilibrado':
        return {
          color: '#a6c10b',
          bg: 'rgba(166, 193, 11, 0.15)',
          border: 'rgba(166, 193, 11, 0.4)',
          icon: <IoCheckmarkCircle size={14} />,
          text: 'Equilibrado',
        }
      case 'Excesso':
        return {
          color: '#ff4d4f',
          bg: 'rgba(255, 77, 79, 0.15)',
          border: 'rgba(255, 77, 79, 0.4)',
          icon: <IoAlertCircle size={14} />,
          text: 'Excesso (> 3m)',
        }
      case 'Ruptura':
        return {
          color: '#ffa940',
          bg: 'rgba(255, 169, 64, 0.15)',
          border: 'rgba(255, 169, 64, 0.4)',
          icon: <IoWarning size={14} />,
          text: 'Ruptura (< 0.5m)',
        }
      default:
        return {
          color: '#8e9aaf',
          bg: 'rgba(142, 154, 175, 0.15)',
          border: 'rgba(142, 154, 175, 0.4)',
          icon: <IoRemoveCircle size={14} />,
          text: 'Sem Giro',
        }
    }
  }

  const getTrendIcon = (trend: string) => {
    if (trend === 'UP') return ArrowGree
    if (trend === 'DOWN') return ArrowRed
    return Neutro
  }

  // Totais agregados
  const totalStats = useMemo(() => {
    let estoqueTotal = 0
    let vendaMediaTotal = 0
    let totalFamiliasCount = 0
    let totalSkusCount = 0

    data.forEach((cat) => {
      estoqueTotal += cat.atual
      vendaMediaTotal += cat.vendaMedia
      totalFamiliasCount += cat.familias.length
      cat.familias.forEach((fam) => {
        totalSkusCount += (fam.skus || []).length
      })
    })

    const cobGeral = vendaMediaTotal > 0 ? estoqueTotal / vendaMediaTotal : 0
    return {
      estoqueTotal,
      vendaMediaTotal,
      totalCategoriasCount: data.length,
      totalFamiliasCount,
      totalSkusCount,
      coberturaGeral: cobGeral,
    }
  }, [data])

  return (
    <Container>
      {/* CABEÇALHO DO MÓDULO HIERÁRQUICO */}
      <HeaderSection>
        <div className='title-group'>
          <h3>
            <span className='dot' /> Performance Hierárquica (Categoria → Família → SKU)
          </h3>
          <p className='subtitle'>
            Estoque Atual x Sellout Médio (3 Meses) estruturado em cartões expansíveis com gráficos comparativos
          </p>
        </div>

        {/* CONTROLES E FILTROS */}
        <ControlsGroup>
          {/* BUSCA */}
          <SearchWrapper>
            <IoSearchOutline className='search-icon' />
            <SearchInput
              type='text'
              placeholder='Buscar categoria, família, SKU, EAN...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className='clear-btn' onClick={() => setSearchTerm('')}>
                ×
              </button>
            )}
          </SearchWrapper>

          {/* BOTÕES EXPANDIR / RECOLHER */}
          <ActionButtonsGroup>
            <ActionButton onClick={handleExpandAll} title='Expandir todas as categorias e famílias'>
              <IoExpandOutline size={14} /> Expandir Tudo
            </ActionButton>
            <ActionButton onClick={handleCollapseAll} title='Recolher tudo'>
              <IoContractOutline size={14} /> Recolher
            </ActionButton>
            {onOpenInfoModal && (
              <InfoButton onClick={onOpenInfoModal} title='Critérios de performance'>
                <IoInformationCircleOutline size={20} />
              </InfoButton>
            )}
          </ActionButtonsGroup>
        </ControlsGroup>
      </HeaderSection>

      {/* BARRA DE FILTROS POR STATUS E CHIPS DE RESUMO */}
      <FilterBar>
        <StatusFiltersContainer>
          <span className='filter-label'>Filtrar por Status:</span>
          {(['TODOS', 'Equilibrado', 'Excesso', 'Ruptura', 'Sem Giro'] as StatusFilterType[]).map(
            (st) => {
              const isActive = statusFilter === st
              return (
                <StatusChip
                  key={st}
                  active={isActive}
                  status={st}
                  onClick={() => setStatusFilter(st)}
                >
                  {st === 'TODOS' ? 'Todos' : st}
                </StatusChip>
              )
            },
          )}
        </StatusFiltersContainer>

        <SummaryPills>
          <SummaryPill>
            <span className='label'>Categorias:</span>
            <span className='value'>{totalStats.totalCategoriasCount}</span>
          </SummaryPill>
          <SummaryPill>
            <span className='label'>Famílias:</span>
            <span className='value'>{totalStats.totalFamiliasCount}</span>
          </SummaryPill>
          <SummaryPill>
            <span className='label'>SKUs:</span>
            <span className='value'>{totalStats.totalSkusCount}</span>
          </SummaryPill>
          <SummaryPill accent>
            <span className='label'>Estoque Total:</span>
            <span className='value'>{Math.round(totalStats.estoqueTotal).toLocaleString('pt-BR')} Cx</span>
          </SummaryPill>
        </SummaryPills>
      </FilterBar>

      {/* LISTA HIERÁRQUICA DE CARDS */}
      <CardsList>
        {filteredData.length === 0 ? (
          <EmptyState>
            <IoFolderOutline size={40} className='empty-icon' />
            <p>Nenhuma categoria ou produto encontrado com os filtros atuais.</p>
            {(searchTerm || statusFilter !== 'TODOS') && (
              <button
                className='reset-btn'
                onClick={() => {
                  setSearchTerm('')
                  setStatusFilter('TODOS')
                }}
              >
                Limpar Filtros
              </button>
            )}
          </EmptyState>
        ) : (
          filteredData.map((cat) => {
            const isCatExpanded = expandedCategories.has(cat.categoria)
            const isCatChartOpen = categoryChartOpen.has(cat.categoria)
            const catStatusCfg = getStatusConfig(cat.status)

            const chartFamilyItems: ChartItemData[] = cat.familias.map((fam) => ({
              label: fam.familia,
              estoque: fam.atual,
              selloutMedio: fam.vendaMedia,
              cobertura: fam.cobertura,
              dias: fam.dias,
            }))

            return (
              <CategoryCard key={cat.categoria} isExpanded={isCatExpanded}>
                {/* CABEÇALHO DO CARD DE CATEGORIA (NÍVEL 1) */}
                <CategoryHeader onClick={() => toggleCategory(cat.categoria)}>
                  <div className='left-col'>
                    <ExpandIconWrapper isExpanded={isCatExpanded}>
                      <IoChevronForward size={16} />
                    </ExpandIconWrapper>

                    <IconBadge color='#a6c10b'>
                      <IoFolderOutline size={18} />
                    </IconBadge>

                    <div className='title-box'>
                      <div className='name-line'>
                        <h4>{cat.categoria}</h4>
                        <span className='count-badge'>
                          {cat.familias.length} {cat.familias.length === 1 ? 'Família' : 'Famílias'} •{' '}
                          {cat.totalSkus} SKUs
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className='metrics-col'>
                    <MetricBlock>
                      <span className='metric-label'>Estoque Atual</span>
                      <span className='metric-value highlight'>
                        {Math.round(cat.atual).toLocaleString('pt-BR')} <small>Cx</small>
                      </span>
                    </MetricBlock>

                    <MetricBlock>
                      <span className='metric-label'>Venda Média (3M)</span>
                      <span className='metric-value'>
                        {cat.vendaMedia.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}{' '}
                        <small>Cx/mês</small>
                      </span>
                    </MetricBlock>

                    <MetricBlock>
                      <span className='metric-label'>Cobertura</span>
                      <span className='metric-value'>
                        {cat.vendaMedia > 0 ? `${cat.cobertura.toFixed(1)} meses` : '∞'}{' '}
                        <small className='days-sub'>({cat.dias} dias)</small>
                      </span>
                    </MetricBlock>

                    <MetricBlock>
                      <span className='metric-label'>Variação</span>
                      <div className='variation-box'>
                        <img
                          src={getTrendIcon(cat.tendencia)}
                          alt='Tendência'
                          style={{ width: '12px', height: '12px' }}
                        />
                        <span className='var-text'>{cat.percentual}%</span>
                      </div>
                    </MetricBlock>

                    <StatusBadge
                      color={catStatusCfg.color}
                      bg={catStatusCfg.bg}
                      border={catStatusCfg.border}
                    >
                      {catStatusCfg.icon}
                      <span>{catStatusCfg.text}</span>
                    </StatusBadge>

                    {/* BOTÃO DE GRÁFICO NO CANTO DO CARD DE CATEGORIA */}
                    <ChartToggleBtn
                      active={isCatChartOpen}
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleCategoryChart(cat.categoria)
                      }}
                      title='Comparativo Gráfico das Famílias'
                    >
                      <IoBarChartOutline size={15} />
                      <span>{isCatChartOpen ? 'Ocultar Gráfico' : 'Gráfico'}</span>
                    </ChartToggleBtn>
                  </div>
                </CategoryHeader>

                {/* PAINEL DE GRÁFICO CONTEXTUAL DA CATEGORIA */}
                {isCatChartOpen && (
                  <EmbeddedCardChart
                    title={`Comparativo por Família: ${cat.categoria}`}
                    subtitle='Estoque Atual (Cx) vs Sellout Médio (3 Meses)'
                    items={chartFamilyItems}
                    onClose={() => toggleCategoryChart(cat.categoria)}
                  />
                )}

                {/* CORPO EXPANSÍVEL: FAMÍLIAS DA CATEGORIA (NÍVEL 2) */}
                {isCatExpanded && (
                  <CategoryBody>
                    {cat.familias.length === 0 ? (
                      <EmptySubList>Nenhuma família cadastrada para esta categoria.</EmptySubList>
                    ) : (
                      cat.familias.map((fam) => {
                        const famKey = `${cat.categoria}__${fam.familia}`
                        const isFamExpanded = expandedFamilies.has(famKey)
                        const isFamChartOpen = familyChartOpen.has(famKey)
                        const famStatusCfg = getStatusConfig(fam.status)
                        const skuList = fam.skus || []

                        const chartSkuItems: ChartItemData[] = skuList.map((sku) => ({
                          label: sku.descricao.length > 25 ? `${sku.descricao.slice(0, 25)}...` : sku.descricao,
                          estoque: sku.atual,
                          selloutMedio: sku.vendaMedia,
                          cobertura: sku.cobertura,
                          dias: sku.dias,
                        }))

                        return (
                          <FamilyCard key={fam.familia} isExpanded={isFamExpanded}>
                            {/* CABEÇALHO DA FAMÍLIA */}
                            <FamilyHeader onClick={() => toggleFamily(cat.categoria, fam.familia)}>
                              <div className='left-col'>
                                <ExpandIconWrapper isExpanded={isFamExpanded} size={14}>
                                  <IoChevronForward size={14} />
                                </ExpandIconWrapper>

                                <IconBadge color='#4cc9f0' size='28px'>
                                  <IoLayersOutline size={15} />
                                </IconBadge>

                                <div className='title-box'>
                                  <div className='name-line'>
                                    <h5>{fam.familia}</h5>
                                    <span className='count-badge sub'>
                                      {skuList.length} {skuList.length === 1 ? 'SKU' : 'SKUs'}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <div className='metrics-col'>
                                <MetricBlock compact>
                                  <span className='metric-label'>Estoque</span>
                                  <span className='metric-value'>
                                    {Math.round(fam.atual).toLocaleString('pt-BR')} <small>Cx</small>
                                  </span>
                                </MetricBlock>

                                <MetricBlock compact>
                                  <span className='metric-label'>Venda Média</span>
                                  <span className='metric-value'>
                                    {fam.vendaMedia.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}{' '}
                                    <small>Cx/mês</small>
                                  </span>
                                </MetricBlock>

                                <MetricBlock compact>
                                  <span className='metric-label'>Cobertura</span>
                                  <span className='metric-value'>
                                    {fam.vendaMedia > 0 ? `${fam.cobertura.toFixed(1)}m` : '∞'}{' '}
                                    <small className='days-sub'>({fam.dias}d)</small>
                                  </span>
                                </MetricBlock>

                                <StatusBadge
                                  compact
                                  color={famStatusCfg.color}
                                  bg={famStatusCfg.bg}
                                  border={famStatusCfg.border}
                                >
                                  {famStatusCfg.icon}
                                  <span>{famStatusCfg.text}</span>
                                </StatusBadge>

                                {/* BOTÃO DE GRÁFICO NO CANTO DO CARD DE FAMÍLIA */}
                                <ChartToggleBtn
                                  compact
                                  active={isFamChartOpen}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    toggleFamilyChart(cat.categoria, fam.familia)
                                  }}
                                  title='Comparativo Gráfico dos SKUs'
                                >
                                  <IoBarChartOutline size={13} />
                                  <span>{isFamChartOpen ? 'Ocultar' : 'Gráfico'}</span>
                                </ChartToggleBtn>
                              </div>
                            </FamilyHeader>

                            {/* PAINEL DE GRÁFICO CONTEXTUAL DA FAMÍLIA (SKUS) */}
                            {isFamChartOpen && (
                              <EmbeddedCardChart
                                title={`Comparativo de SKUs: ${fam.familia}`}
                                subtitle='Estoque Atual (Cx) vs Sellout Médio (3 Meses)'
                                items={chartSkuItems}
                                onClose={() => toggleFamilyChart(cat.categoria, fam.familia)}
                              />
                            )}

                            {/* CORPO EXPANSÍVEL: TABELA DE SKUS (NÍVEL 3) */}
                            {isFamExpanded && (
                              <FamilyBody>
                                {skuList.length === 0 ? (
                                  <EmptySubList>Nenhum produto (SKU) encontrado para esta família.</EmptySubList>
                                ) : (
                                  <SkuTableWrapper>
                                    <SkuTable>
                                      <thead>
                                        <tr>
                                          <th>Produto / SKU</th>
                                          <th>Marca</th>
                                          <th style={{ textAlign: 'center' }}>Un/Cx</th>
                                          <th style={{ textAlign: 'right' }}>Estoque Atual (Cx)</th>
                                          <th style={{ textAlign: 'right' }}>Venda Média (Cx/mês)</th>
                                          <th style={{ textAlign: 'right' }}>Cobertura</th>
                                          <th style={{ textAlign: 'center' }}>Status</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {skuList.map((sku, sIdx) => {
                                          const skuStatusCfg = getStatusConfig(sku.status)
                                          return (
                                            <tr key={`${sku.sku}_${sku.ean}_${sIdx}`}>
                                              <td>
                                                <SkuNameGroup>
                                                  <span className='sku-desc' title={sku.descricao}>
                                                    {sku.descricao}
                                                  </span>
                                                  <div className='sku-tags'>
                                                    {sku.sku && <span className='sku-chip'>SKU: {sku.sku}</span>}
                                                    {sku.ean && <span className='ean-chip'>EAN: {sku.ean}</span>}
                                                  </div>
                                                </SkuNameGroup>
                                              </td>
                                              <td>
                                                <span className='brand-label'>{sku.marca || '-'}</span>
                                              </td>
                                              <td style={{ textAlign: 'center' }}>{sku.un_cx || 1}</td>
                                              <td style={{ textAlign: 'right', fontWeight: 'bold', color: isLight ? '#0f172a' : '#fff' }}>
                                                {sku.atual.toLocaleString('pt-BR')}
                                              </td>
                                              <td style={{ textAlign: 'right', color: isLight ? '#0f172a' : '#cbd5e1' }}>
                                                {sku.vendaMedia.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}
                                              </td>
                                              <td style={{ textAlign: 'right', color: isLight ? '#0f172a' : '#cbd5e1' }}>
                                                {sku.vendaMedia > 0 ? (
                                                  <span>
                                                    <strong>{sku.cobertura.toFixed(1)}</strong> m{' '}
                                                    <small style={{ color: isLight ? '#64748b' : '#8e9aaf' }}>({sku.dias}d)</small>
                                                  </span>
                                                ) : (
                                                  <span style={{ color: '#ff4d4f' }}>Sem saída</span>
                                                )}
                                              </td>
                                              <td style={{ textAlign: 'center' }}>
                                                <StatusBadge
                                                  mini
                                                  color={skuStatusCfg.color}
                                                  bg={skuStatusCfg.bg}
                                                  border={skuStatusCfg.border}
                                                >
                                                  {skuStatusCfg.icon}
                                                  <span>{skuStatusCfg.text}</span>
                                                </StatusBadge>
                                              </td>
                                            </tr>
                                          )
                                        })}
                                      </tbody>
                                    </SkuTable>
                                  </SkuTableWrapper>
                                )}
                              </FamilyBody>
                            )}
                          </FamilyCard>
                        )
                      })
                    )}
                  </CategoryBody>
                )}
              </CategoryCard>
            )
          })
        )}
      </CardsList>
    </Container>
  )
}

// --- ESTILOS DOS CARDS HIERÁRQUICOS ---

const Container = styled.div`
  margin-bottom: 25px;
  font-family: 'Montserrat', sans-serif;
`

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 15px;

  .title-group {
    h3 {
      color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#fff')};
      font-size: 16px;
      font-weight: 700;
      margin: 0 0 4px 0;
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .dot {
      width: 10px;
      height: 10px;
      background: #a6c10b;
      border-radius: 50%;
      box-shadow: 0 0 12px #a6c10b;
    }
    .subtitle {
      color: ${(props) => (props.theme.name === 'light' ? '#64748b' : '#8e9aaf')};
      font-size: 12px;
      margin: 0;
    }
  }
`

const ControlsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

const SearchWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  .search-icon {
    position: absolute;
    left: 12px;
    color: ${(props) => (props.theme.name === 'light' ? '#64748b' : '#8e9aaf')};
    font-size: 15px;
  }

  .clear-btn {
    position: absolute;
    right: 8px;
    background: transparent;
    border: none;
    color: ${(props) => (props.theme.name === 'light' ? '#64748b' : '#8e9aaf')};
    font-size: 16px;
    cursor: pointer;
    padding: 2px 6px;
    border-radius: 50%;
    &:hover {
      color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#fff')};
      background: ${(props) => (props.theme.name === 'light' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)')};
    }
  }
`

const SearchInput = styled.input`
  background: ${(props) => (props.theme.name === 'light' ? '#ffffff' : 'rgba(0, 29, 83, 0.5)')};
  border: 1px solid ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)')};
  border-radius: 10px;
  padding: 8px 30px 8px 34px;
  color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#fff')};
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  outline: none;
  width: 260px;
  transition: all 0.3s ease;

  &:focus {
    border-color: #a6c10b;
    background: ${(props) => (props.theme.name === 'light' ? '#ffffff' : 'rgba(0, 29, 83, 0.8)')};
    box-shadow: 0 0 12px rgba(166, 193, 11, 0.25);
    width: 290px;
  }

  &::placeholder {
    color: ${(props) => (props.theme.name === 'light' ? '#94a3b8' : 'rgba(255, 255, 255, 0.35)')};
  }

  @media (max-width: 768px) {
    width: 100%;
    &:focus {
      width: 100%;
    }
  }
`

const ActionButtonsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ActionButton = styled.button`
  background: ${(props) => (props.theme.name === 'light' ? '#ffffff' : 'rgba(255, 255, 255, 0.05)')};
  border: 1px solid ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)')};
  color: ${(props) => (props.theme.name === 'light' ? '#1e293b' : '#cbd5e1')};
  box-shadow: ${(props) => (props.theme.name === 'light' ? '0 1px 3px rgba(0,0,0,0.05)' : 'none')};
  padding: 7px 12px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  font-family: 'Montserrat', sans-serif;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(166, 193, 11, 0.15);
    border-color: #a6c10b;
    color: ${(props) => (props.theme.name === 'light' ? '#4d7c0f' : '#a6c10b')};
  }
`

const InfoButton = styled.button`
  background: transparent;
  border: none;
  color: #a6c10b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.15);
  }
`

const FilterBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  background: ${(props) => (props.theme.name === 'light' ? '#ffffff' : 'rgba(0, 0, 0, 0.25)')};
  padding: 10px 16px;
  border-radius: 12px;
  border: 1px solid ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.06)')};
  box-shadow: ${(props) => (props.theme.name === 'light' ? '0 2px 8px rgba(0,0,0,0.04)' : 'none')};
  margin-bottom: 15px;
`

const StatusFiltersContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  .filter-label {
    color: ${(props) => (props.theme.name === 'light' ? '#475569' : '#8e9aaf')};
    font-size: 11px;
    font-weight: 600;
    margin-right: 4px;
  }
`

const StatusChip = styled.button<{ active: boolean; status: string }>`
  background: ${(props) => {
    if (!props.active) return props.theme.name === 'light' ? '#f1f5f9' : 'rgba(255, 255, 255, 0.04)'
    if (props.status === 'Equilibrado') return 'rgba(166, 193, 11, 0.2)'
    if (props.status === 'Excesso') return 'rgba(255, 77, 79, 0.2)'
    if (props.status === 'Ruptura') return 'rgba(255, 169, 64, 0.2)'
    if (props.status === 'Sem Giro') return props.theme.name === 'light' ? 'rgba(100, 116, 139, 0.2)' : 'rgba(142, 154, 175, 0.2)'
    return props.theme.name === 'light' ? 'rgba(0, 31, 92, 0.15)' : 'rgba(255, 255, 255, 0.2)'
  }};
  color: ${(props) => {
    if (!props.active) return props.theme.name === 'light' ? '#475569' : '#94a3b8'
    if (props.status === 'Equilibrado') return props.theme.name === 'light' ? '#4d7c0f' : '#a6c10b'
    if (props.status === 'Excesso') return '#ff4d4f'
    if (props.status === 'Ruptura') return '#ffa940'
    if (props.status === 'Sem Giro') return props.theme.name === 'light' ? '#475569' : '#cbd5e1'
    return props.theme.name === 'light' ? '#0f172a' : '#fff'
  }};
  border: 1px solid
    ${(props) => {
      if (!props.active) return props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.08)'
      if (props.status === 'Equilibrado') return '#a6c10b'
      if (props.status === 'Excesso') return '#ff4d4f'
      if (props.status === 'Ruptura') return '#ffa940'
      if (props.status === 'Sem Giro') return props.theme.name === 'light' ? '#94a3b8' : '#cbd5e1'
      return props.theme.name === 'light' ? '#001f5c' : '#fff'
    }};
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: ${(props) => (props.active ? '700' : '500')};
  font-family: 'Montserrat', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${(props) => (props.theme.name === 'light' ? '#94a3b8' : 'rgba(255, 255, 255, 0.3)')};
    color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#fff')};
  }
`

const SummaryPills = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`

const SummaryPill = styled.div<{ accent?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: ${(props) => (props.theme.name === 'light' ? '#475569' : '#94a3b8')};
  background: ${(props) =>
    props.accent
      ? props.theme.name === 'light'
        ? 'rgba(166, 193, 11, 0.18)'
        : 'rgba(166, 193, 11, 0.1)'
      : props.theme.name === 'light'
      ? '#f1f5f9'
      : 'rgba(255, 255, 255, 0.03)'};
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid
    ${(props) =>
      props.accent
        ? props.theme.name === 'light'
          ? '#a6c10b'
          : 'rgba(166, 193, 11, 0.3)'
        : props.theme.name === 'light'
        ? '#cbd5e1'
        : 'rgba(255, 255, 255, 0.05)'};

  .label {
    font-weight: 500;
  }
  .value {
    color: ${(props) =>
      props.accent
        ? props.theme.name === 'light'
          ? '#4d7c0f'
          : '#a6c10b'
        : props.theme.name === 'light'
        ? '#0f172a'
        : '#fff'};
    font-weight: 700;
  }
`

const CardsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const EmptyState = styled.div`
  background: ${(props) => (props.theme.name === 'light' ? '#ffffff' : 'rgba(0, 29, 83, 0.3)')};
  border: 1px dashed ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)')};
  border-radius: 16px;
  padding: 40px 20px;
  text-align: center;
  color: ${(props) => (props.theme.name === 'light' ? '#64748b' : '#8e9aaf')};

  .empty-icon {
    color: #475569;
    margin-bottom: 12px;
  }

  p {
    font-size: 13px;
    margin: 0 0 15px 0;
  }

  .reset-btn {
    background: #a6c10b;
    color: #000;
    border: none;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: bold;
    cursor: pointer;
    font-family: 'Montserrat', sans-serif;
  }
`

// --- NÍVEL 1: CATEGORIA ---

const CategoryCard = styled.div<{ isExpanded: boolean }>`
  background: ${(props) =>
    props.theme.name === 'light'
      ? '#ffffff'
      : 'linear-gradient(135deg, rgba(0, 29, 83, 0.6) 0%, rgba(5, 18, 48, 0.85) 100%)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'light'
        ? props.isExpanded
          ? '#a6c10b'
          : '#cbd5e1'
        : props.isExpanded
        ? 'rgba(166, 193, 11, 0.4)'
        : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 14px;
  overflow: hidden;
  box-shadow: ${(props) =>
    props.theme.name === 'light'
      ? '0 4px 15px rgba(0, 0, 0, 0.05)'
      : '0 4px 20px rgba(0, 0, 0, 0.25)'};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: ${(props) => (props.theme.name === 'light' ? '#a6c10b' : 'rgba(166, 193, 11, 0.5)')};
    box-shadow: ${(props) =>
      props.theme.name === 'light'
        ? '0 6px 20px rgba(0, 0, 0, 0.08)'
        : '0 6px 24px rgba(0, 0, 0, 0.35)'};
  }
`

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  cursor: pointer;
  user-select: none;
  background: ${(props) => (props.theme.name === 'light' ? '#ffffff' : 'rgba(255, 255, 255, 0.02)')};
  transition: background 0.2s ease;
  flex-wrap: wrap;
  gap: 15px;

  &:hover {
    background: ${(props) => (props.theme.name === 'light' ? '#f8fafc' : 'rgba(255, 255, 255, 0.06)')};
  }

  .left-col {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .title-box {
    .name-line {
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;

      h4 {
        margin: 0;
        color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#fff')};
        font-size: 15px;
        font-weight: 700;
        letter-spacing: 0.3px;
      }

      .count-badge {
        background: ${(props) => (props.theme.name === 'light' ? '#f1f5f9' : 'rgba(255, 255, 255, 0.08)')};
        color: ${(props) => (props.theme.name === 'light' ? '#475569' : '#94a3b8')};
        padding: 3px 8px;
        border-radius: 6px;
        font-size: 11px;
        font-weight: 600;
        border: 1px solid ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.08)')};
      }
    }
  }

  .metrics-col {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
  }
`

const CategoryBody = styled.div`
  padding: 12px 18px 16px 18px;
  background: ${(props) => (props.theme.name === 'light' ? '#f8fafc' : 'rgba(0, 0, 0, 0.2)')};
  border-top: 1px solid ${(props) => (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.06)')};
  display: flex;
  flex-direction: column;
  gap: 10px;
`

// --- NÍVEL 2: FAMÍLIA ---

const FamilyCard = styled.div<{ isExpanded: boolean }>`
  background: ${(props) => (props.theme.name === 'light' ? '#ffffff' : 'rgba(255, 255, 255, 0.03)')};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'light'
        ? props.isExpanded
          ? '#0284c7'
          : '#cbd5e1'
        : props.isExpanded
        ? 'rgba(76, 201, 240, 0.35)'
        : 'rgba(255, 255, 255, 0.06)'};
  border-left: 3px solid
    ${(props) => (props.isExpanded ? '#0284c7' : '#a6c10b')};
  border-radius: 10px;
  overflow: hidden;
  transition: all 0.2s ease;
  box-shadow: ${(props) => (props.theme.name === 'light' ? '0 1px 4px rgba(0,0,0,0.04)' : 'none')};

  &:hover {
    background: ${(props) => (props.theme.name === 'light' ? '#ffffff' : 'rgba(255, 255, 255, 0.05)')};
    border-color: ${(props) => (props.theme.name === 'light' ? '#0284c7' : 'rgba(255, 255, 255, 0.15)')};
  }
`

const FamilyHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  cursor: pointer;
  user-select: none;
  flex-wrap: wrap;
  gap: 12px;

  .left-col {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .title-box {
    .name-line {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;

      h5 {
        margin: 0;
        color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#f1f5f9')};
        font-size: 13px;
        font-weight: 600;
      }

      .count-badge.sub {
        background: ${(props) => (props.theme.name === 'light' ? 'rgba(2, 132, 199, 0.1)' : 'rgba(76, 201, 240, 0.1)')};
        color: ${(props) => (props.theme.name === 'light' ? '#0284c7' : '#4cc9f0')};
        padding: 2px 6px;
        border-radius: 4px;
        font-size: 10px;
        font-weight: 600;
        border: 1px solid ${(props) => (props.theme.name === 'light' ? 'rgba(2, 132, 199, 0.25)' : 'rgba(76, 201, 240, 0.25)')};
      }
    }
  }

  .metrics-col {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
  }
`

const FamilyBody = styled.div`
  padding: 10px 14px 14px 14px;
  background: ${(props) => (props.theme.name === 'light' ? '#f1f5f9' : 'rgba(0, 0, 0, 0.35)')};
  border-top: 1px solid ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.04)')};
`

// --- NÍVEL 3: TABELA DE SKUS ---

const SkuTableWrapper = styled.div`
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.06)')};

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #a6c10b;
    border-radius: 10px;
  }
`

const SkuTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#cbd5e1')};

  th {
    background: ${(props) => (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(0, 29, 83, 0.9)')};
    color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#a6c10b')};
    padding: 8px 10px;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 10px;
    letter-spacing: 0.4px;
    border-bottom: 1px solid ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.08)')};
  }

  td {
    padding: 8px 10px;
    border-bottom: 1px solid ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.03)')};
    vertical-align: middle;
  }

  tr:hover td {
    background: ${(props) => (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.04)')};
  }

  tr:last-child td {
    border-bottom: none;
  }
`

const SkuNameGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;

  .sku-desc {
    color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#fff')};
    font-weight: 600;
    font-size: 12px;
    max-width: 320px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .sku-tags {
    display: flex;
    gap: 6px;
    align-items: center;
    flex-wrap: wrap;
  }

  .sku-chip {
    background: ${(props) => (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.06)')};
    color: ${(props) => (props.theme.name === 'light' ? '#475569' : '#94a3b8')};
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 9px;
    font-family: monospace;
  }

  .ean-chip {
    background: rgba(166, 193, 11, 0.12);
    color: ${(props) => (props.theme.name === 'light' ? '#4d7c0f' : '#a6c10b')};
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 9px;
    font-family: monospace;
  }

  .brand-label {
    color: ${(props) => (props.theme.name === 'light' ? '#475569' : '#cbd5e1')};
    font-size: 11px;
  }
`

const EmptySubList = styled.div`
  padding: 15px;
  text-align: center;
  color: #64748b;
  font-size: 12px;
  font-style: italic;
`

// --- PAINEL DE GRÁFICO EMBUTIDO ---

const ChartContainer = styled.div`
  background: ${(props) =>
    props.theme.name === 'light'
      ? '#ffffff'
      : 'linear-gradient(135deg, rgba(0, 15, 45, 0.95) 0%, rgba(0, 25, 65, 0.95) 100%)'};
  border: 1px solid ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(166, 193, 11, 0.3)')};
  border-radius: 12px;
  padding: 14px 16px 8px 16px;
  margin: 10px 18px 14px 18px;
  box-shadow: ${(props) =>
    props.theme.name === 'light'
      ? '0 4px 15px rgba(0, 0, 0, 0.06)'
      : 'inset 0 0 25px rgba(0, 0, 0, 0.5), 0 4px 15px rgba(0, 0, 0, 0.3)'};
`

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid ${(props) => (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};

  .title-area {
    h6 {
      color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#fff')};
      font-size: 13px;
      font-weight: 700;
      margin: 0;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .sub {
      color: ${(props) => (props.theme.name === 'light' ? '#64748b' : '#8e9aaf')};
      font-size: 11px;
      margin-left: 21px;
    }
  }
`

const CloseChartBtn = styled.button`
  background: transparent;
  border: none;
  color: ${(props) => (props.theme.name === 'light' ? '#64748b' : '#8e9aaf')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    color: #ff4d4f;
    background: rgba(255, 77, 79, 0.15);
  }
`

const ChartScrollWrapper = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 4px;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(166, 193, 11, 0.5);
    border-radius: 6px;
  }
`

const ChartToggleBtn = styled.button<{ active?: boolean; compact?: boolean }>`
  background: ${(props) =>
    props.active
      ? 'rgba(166, 193, 11, 0.25)'
      : props.theme.name === 'light'
      ? '#ffffff'
      : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid
    ${(props) =>
      props.active
        ? '#a6c10b'
        : props.theme.name === 'light'
        ? '#cbd5e1'
        : 'rgba(255, 255, 255, 0.15)'};
  color: ${(props) =>
    props.active
      ? props.theme.name === 'light'
        ? '#4d7c0f'
        : '#a6c10b'
      : props.theme.name === 'light'
      ? '#1e293b'
      : '#cbd5e1'};
  padding: ${(props) => (props.compact ? '3px 8px' : '5px 10px')};
  border-radius: 6px;
  font-size: ${(props) => (props.compact ? '10px' : '11px')};
  font-weight: 700;
  font-family: 'Montserrat', sans-serif;
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(166, 193, 11, 0.2);
    border-color: #a6c10b;
    color: ${(props) => (props.theme.name === 'light' ? '#4d7c0f' : '#a6c10b')};
  }
`

// --- ELEMENTOS VISUAIS REUTILIZÁVEIS ---

const ExpandIconWrapper = styled.div<{ isExpanded: boolean; size?: number }>`
  color: ${(props) => (props.isExpanded ? '#a6c10b' : props.theme.name === 'light' ? '#64748b' : '#94a3b8')};
  transform: ${(props) => (props.isExpanded ? 'rotate(90deg)' : 'rotate(0deg)')};
  transition: transform 0.2s ease, color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
`

const IconBadge = styled.div<{ color: string; size?: string }>`
  width: ${(props) => props.size || '32px'};
  height: ${(props) => props.size || '32px'};
  border-radius: 8px;
  background: ${(props) => `${props.color}15`};
  border: 1px solid ${(props) => `${props.color}35`};
  color: ${(props) => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const MetricBlock = styled.div<{ compact?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  min-width: ${(props) => (props.compact ? '65px' : '80px')};

  .metric-label {
    color: ${(props) => (props.theme.name === 'light' ? '#64748b' : '#8e9aaf')};
    font-size: ${(props) => (props.compact ? '9px' : '10px')};
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.3px;
  }

  .metric-value {
    color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#cbd5e1')};
    font-size: ${(props) => (props.compact ? '12px' : '13px')};
    font-weight: 700;

    small {
      font-size: 10px;
      color: ${(props) => (props.theme.name === 'light' ? '#64748b' : '#8e9aaf')};
      font-weight: normal;
    }

    .days-sub {
      color: ${(props) => (props.theme.name === 'light' ? '#64748b' : '#64748b')};
      font-size: 10px;
      margin-left: 2px;
    }
  }

  .metric-value.highlight {
    color: ${(props) => (props.theme.name === 'light' ? '#001f5c' : '#fff')};
    font-size: ${(props) => (props.compact ? '13px' : '14px')};
  }

  .variation-box {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: bold;
    color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#fff')};
  }
`

const StatusBadge = styled.div<{
  color: string
  bg: string
  border: string
  compact?: boolean
  mini?: boolean
}>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: ${(props) => props.bg};
  color: ${(props) => props.color};
  border: 1px solid ${(props) => props.border};
  padding: ${(props) => (props.mini ? '2px 6px' : props.compact ? '3px 8px' : '5px 10px')};
  border-radius: 6px;
  font-size: ${(props) => (props.mini ? '10px' : props.compact ? '11px' : '11px')};
  font-weight: 700;
  white-space: nowrap;
`
